import { MVP_ITEMS, type MvpItemData, type MvpItemId } from '../items/mvpItems';
import { currentItemId } from '../items/legacy';
import type { GearSlot, ItemStatKey, ItemStats, Rarity, SetId, WeaponClass } from '../types';

export interface ItemTemplate {
  id: string;
  name: string;
  slot: GearSlot;
  /** T1 = Lv 1–5, T2 = Lv 6–10, T3 = Lv 11–15, T4 = Lv 16–20. */
  tier: 1 | 2 | 3 | 4;
  weaponClass?: WeaponClass;
  loud?: boolean;
  /** Armor set (helmet, body, gloves, pants, boots). */
  setId?: SetId;
  /** Folder under /assets/icons/{size}/; null while the item has no WebP icon. */
  iconFolder: string | null;
  /** Stats at Common. The headline stat (damage, armor or capacity) is the MVP `base`. */
  base: ItemStats;
  /** Equipment load while worn (section 7); the same for every rarity. */
  weight: number;
  /** Name of the Legendary version (Item Catalog). */
  legendaryName?: string;
  flavor: string;
}

/** Stat multiplier by rarity (CHANGELOG 0.2.0, section 5). */
export const RARITY_MULT: Record<Rarity, number> = { common: 1, uncommon: 1.1, rare: 1.25, epic: 1.45, legendary: 1.7 };
/** 7. 10. 2026 (owner): a Legendary has one affix more than an Epic (was 3, as many as an Epic). */
export const AFFIX_COUNT: Record<Rarity, number> = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4 };
/**
 * 7. 10. 2026 (owner, answer c): a Legendary's extra affix (the one over an Epic's count) rolls only from the stats that
 * are not in a fight, so a full Legendary set does not lift Power (a combat 4th affix gave +19.6% at L20, over the +10%
 * limit). The others roll from the whole pool as before.
 */
export const NON_COMBAT_AFFIX_STATS: readonly ItemStatKey[] = ['loot', 'xp', 'expeditionTime', 'durability', 'detect', 'escape', 'encounter', 'stealth', 'discovery', 'luck', 'craftSpeed', 'capacity'];

/** How many of an item's affixes roll from the whole pool; the rest (a Legendary's extra one) are non-combat only. */
export function combatAffixCount(slot: GearSlot, rarity: Rarity): number {
  return rarity === 'legendary' ? affixCount(slot, 'epic') : affixCount(slot, rarity);
}
/**
 * Accessories have no base stat in the Item Catalog, so they roll one affix more than other
 * items (Common 1 … Legendary 4; krug 5, F10).
 */
export function affixCount(slot: GearSlot, rarity: Rarity): number {
  return AFFIX_COUNT[rarity] + (slot === 'accessory' ? 1 : 0);
}
export const RARITY_RANK: Record<Rarity, number> = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4 };
export const RARITY_NAME: Record<Rarity, string> = { common: 'Common', uncommon: 'Uncommon', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' };
/** Frame colours from the style brief. */
export const RARITY_COLOR: Record<Rarity, string> = { common: '#B8B5AA', uncommon: '#6FBF4B', rare: '#3D8BE0', epic: '#A259E0', legendary: '#F08A24' };

/**
 * Backpack capacity a gear item takes while it is carried home as loot. This is
 * not the equipment load (`ItemTemplate.weight`): capacity stays for loot only.
 */
export const PACK_SPACE: Record<GearSlot, number> = { weapon: 3, helmet: 2, body: 3, gloves: 1, pants: 2, boots: 2, backpack: 2, accessory: 1 };
export const SLOT_NAME: Record<GearSlot, string> = { weapon: 'Weapon', helmet: 'Helmet', body: 'Body', gloves: 'Gloves', pants: 'Pants', boots: 'Boots', backpack: 'Backpack', accessory: 'Accessory' };

/** Slots shown locked, never dropped or equipped. Empty since krug 5 (F10) opened the accessory slot. */
export const LOCKED_SLOTS: readonly GearSlot[] = [];

export function isSlotLocked(slot: GearSlot): boolean {
  return LOCKED_SLOTS.includes(slot);
}

/** The stat an item's `base` stands for. */
export function headlineStat(slot: GearSlot): 'damage' | 'armor' | 'capacity' | null {
  if (slot === 'weapon') return 'damage';
  if (slot === 'backpack') return 'capacity';
  if (slot === 'accessory') return null;
  return 'armor';
}

/** Stats that scale with rarity and are shown as whole numbers. */
export const HEADLINE_STATS: ItemStatKey[] = ['damage', 'armor', 'capacity', 'hp'];
/** Attribute stats. */
export const ATTR_SCALED: ItemStatKey[] = ['str', 'agi', 'int', 'vit', 'per'];

/**
 * Every weapon of a class shares its handling. Ranged weapons already get the
 * Headshot skill, so melee gets the crit edge (tuned with `pnpm sim`: worst
 * equal-Power match-up ~79%, same as before the MVP catalog).
 */
export const WEAPON_CLASS_TRAITS: Record<WeaponClass, ItemStats> = {
  melee_1h: { crit: 0.04 },
  melee_2h: { crit: 0.03, accuracy: -0.02 },
  ranged_1h: {},
  ranged_2h: {},
};

interface ItemDetails {
  name: string;
  legendaryName: string;
  flavor: string;
  /** Gunfire and engines: more fights, easier to spot. */
  loud?: boolean;
  /** Small signature stats on top of the headline stat. */
  extra?: ItemStats;
}

const DETAILS: Record<MvpItemId, ItemDetails> = {
  // Melee, one hand
  kitchen_knife: { name: 'Kitchen Knife', legendaryName: 'Chef’s Grudge', flavor: 'Still smells faintly of onions.' },
  pipe_wrench: { name: 'Pipe Wrench', legendaryName: 'Plumber’s Oath', flavor: 'Plumbing, repurposed.' },
  crowbar: { name: 'Crowbar', legendaryName: 'Skeleton Key', flavor: 'Opens doors, crates and arguments.' },
  machete: { name: 'Machete', legendaryName: 'Greenmaw', flavor: 'Clears brush. Clears doorways.' },
  combat_knife: { name: 'Combat Knife', legendaryName: 'Whisper', flavor: 'Balanced, blackened, and never loud.' },
  shock_machete: { name: 'Shock Machete', legendaryName: 'Arclight', flavor: 'A car battery taped to the handle. It hums.' },
  // 11. 10. 2026 (owner, BACKLOG #36): the item package; flavor from the world book, Legendary names from docs/recalibrate.md.
  foundry_cleaver: { name: 'Foundry Cleaver', legendaryName: 'Furnace Tongue', flavor: 'A furnace-forged blade with a welded grip.' },
  // Melee, two hands
  nail_bat: { name: 'Nail Bat', legendaryName: 'Home Run', flavor: 'Old sport, new rules.' },
  shovel: { name: 'Shovel', legendaryName: 'Gravedigger', flavor: 'Digs graves. Fills them too.' },
  sledgehammer: { name: 'Sledgehammer', legendaryName: 'Wallbreaker', flavor: 'Every problem is a wall if you swing hard enough.' },
  sign_glaive: { name: 'Sign Glaive', legendaryName: 'Right of Way', flavor: 'A stop sign on a pole. It means it.' },
  fire_axe: { name: 'Fire Axe', legendaryName: 'Inferno', flavor: 'Break glass in case of everything.' },
  chainsaw: { name: 'Chainsaw', legendaryName: 'Lullaby', flavor: 'Wakes the whole block. Worth it.', loud: true },
  // BACKLOG #36: the quiet answer to the Chainsaw (no Fuel, no noise).
  breaker_sledge: { name: 'Breaker Sledge', legendaryName: 'Last Shift', flavor: 'A Steelworks sledge with a slag-iron head.' },
  // Ranged, one hand
  slingshot: { name: 'Slingshot', legendaryName: 'Giant’s Bane', flavor: 'Ball bearings and patience.' },
  flare_gun: { name: 'Flare Gun', legendaryName: 'Beacon', flavor: 'One shot, very bright, everybody looks.', loud: true },
  scrap_pistol: { name: 'Scrap Pistol', legendaryName: 'Junk Genius', flavor: 'Held together with copper wire and faith.', loud: true },
  revolver: { name: 'Revolver', legendaryName: 'Sixth Chamber', flavor: 'Six chances. Count them.', loud: true },
  service_pistol: { name: 'Service Pistol', legendaryName: 'Quiet Diplomacy', flavor: 'Police issue. The badge is long gone.', loud: true },
  hand_cannon: { name: 'Hand Cannon', legendaryName: 'Judgment', flavor: 'Kicks like a mule, lands like a truck.', loud: true },
  // BACKLOG #36: quiet one-handers for T2–T4 (no Ammo, `QUIET_RANGED`).
  throwing_knives: { name: 'Throwing Knives', legendaryName: 'Five Favors', flavor: 'A bandolier of balanced blades. Pick them up after.' },
  hand_crossbow: { name: 'Hand Crossbow', legendaryName: 'Pocket Verdict', flavor: 'Fits under a coat. Tollmen hate it.' },
  halcyon_dart_pistol: { name: 'Halcyon Dart Pistol', legendaryName: 'Sweet Dreams', flavor: 'Halcyon used it on patients who ran.' },
  // Ranged, two hands
  recurve_bow: { name: 'Recurve Bow', legendaryName: 'Heartwood', flavor: 'Quiet, patient, and it never jams.' },
  pipe_shotgun: { name: 'Pipe Shotgun', legendaryName: 'Backyard Thunder', flavor: 'Two pipes and a nail for a firing pin.', loud: true },
  longshot_rifle: { name: 'Longshot Rifle', legendaryName: 'Ember', flavor: 'Loud, heavy, and it always starts the conversation.', loud: true },
  doorbreaker_shotgun: { name: 'Doorbreaker Shotgun', legendaryName: 'Housecall', flavor: 'Knocks politely. Once.', loud: true, extra: { vsInfected: 0.1 } },
  stalker_crossbow: { name: 'Stalker Crossbow', legendaryName: 'The Quiet One', flavor: 'Quiet enough to hear them breathe.' },
  r12_service_carbine: { name: 'R-12 Service Carbine', legendaryName: 'Iron Discipline', flavor: 'Serial number filed down to scratches.', loud: true, extra: { vsRaider: 0.08 } },
  // BACKLOG #36: quiet two-handers for T2 and T4.
  compound_bow: { name: 'Compound Bow', legendaryName: 'Long Silence', flavor: 'Pulleys off a garage door. Quiet as a held breath.' },
  foundry_arbalest: { name: 'Foundry Arbalest', legendaryName: 'Slag Oath', flavor: 'Steel and cable from the Steelworks, for jobs the Foundry wants unheard.' },
  // Drifter
  bandana_goggles: { name: 'Bandana & Goggles', legendaryName: 'Dust Devil', flavor: 'Keeps the dust out. Mostly.' },
  patched_hoodie: { name: 'Patched Hoodie', legendaryName: 'Second Skin', flavor: 'Three kinds of tape. None of them waterproof.' },
  fingerless_gloves: { name: 'Fingerless Gloves', legendaryName: 'Quickfingers', flavor: 'Warm knuckles, free trigger finger.' },
  torn_jeans: { name: 'Torn Jeans', legendaryName: 'Lucky Denim', flavor: 'More hole than denim. Still the lucky pair.' },
  worn_sneakers: { name: 'Worn Sneakers', legendaryName: 'Runaway', flavor: 'The left one squeaks.' },
  // Worker
  hard_hat: { name: 'Hard Hat', legendaryName: 'Foreman’s Crown', flavor: 'Rated for falling objects. Close enough.' },
  hi_vis_vest: { name: 'Hi-Vis Vest', legendaryName: 'Overtime', flavor: 'Be seen. Mostly by the wrong people.' },
  work_gloves: { name: 'Work Gloves', legendaryName: 'Iron Grip', flavor: 'Four fingers still work.' },
  cargo_work_pants: { name: 'Cargo Work Pants', legendaryName: 'Bottomless Pockets', flavor: 'Eleven pockets. You checked them all twice.' },
  steel_toe_boots: { name: 'Steel-Toe Boots', legendaryName: 'Kickback', flavor: 'Steel toe. Steel patience.' },
  // Scrapper
  // Rider (krug 5, F10)
  moto_helmet: { name: 'Moto Helmet', legendaryName: 'Road Ghost', flavor: 'Cracked visor. Still flips up with a click.' },
  leather_rider_jacket: { name: 'Leather Rider Jacket', legendaryName: 'Open Road', flavor: 'Old brown leather that remembers highways.' },
  riding_gloves: { name: 'Riding Gloves', legendaryName: 'Throttle', flavor: 'Grip like the handlebars never left.' },
  leather_pants: { name: 'Leather Pants', legendaryName: 'Kickstart', flavor: 'Scuffed at the knees from a hundred stops.' },
  rider_boots: { name: 'Rider Boots', legendaryName: 'Wanderlust', flavor: 'Tall, buckled, made for leaving.' },
  // Hunter (krug 5, F10)
  ghillie_hood: { name: 'Ghillie Hood', legendaryName: 'Wildsight', flavor: 'Net, twigs and leaves. Mostly leaves.' },
  hunting_jacket: { name: 'Hunting Jacket', legendaryName: 'Apex', flavor: 'Camo, orange patches, a pocket for everything.' },
  hunting_gloves: { name: 'Hunting Gloves', legendaryName: 'Steady Hand', flavor: 'Thin leather. You feel the trigger.' },
  camo_trousers: { name: 'Camo Trousers', legendaryName: 'Tall Grass', flavor: 'Muddy to the knee. Blends in anyway.' },
  hiking_boots: { name: 'Hiking Boots', legendaryName: 'Trailblazer', flavor: 'Thick soles, trail mud, red laces.' },
  bucket_helm: { name: 'Bucket Helm', legendaryName: 'Tin Crown', flavor: 'Eye slits cut with a can opener.' },
  road_sign_vest: { name: 'Road Sign Vest', legendaryName: 'Stop Sign', flavor: 'Yield. Or don’t.' },
  wire_gauntlets: { name: 'Wire Gauntlets', legendaryName: 'Rustfist', flavor: 'Barbed wire, wrapped with care.' },
  tire_plate_pants: { name: 'Tire-Plate Pants', legendaryName: 'Retread', flavor: 'Tread on the shins. Grip for days.' },
  tread_boots: { name: 'Tread Boots', legendaryName: 'Tank Treads', flavor: 'Soles cut from a truck tire.' },
  // Medic
  headlamp_cap: { name: 'Headlamp Cap', legendaryName: 'Lantern of Mercy', flavor: 'Light for the stitches. Batteries not included.' },
  paramedic_jacket: { name: 'Paramedic Jacket', legendaryName: 'Lifeline', flavor: 'Reflective stripes, scuffed dull on purpose.' },
  nitrile_gloves: { name: 'Nitrile Gloves', legendaryName: 'Healing Touch', flavor: 'A fresh pair is a small luxury now.' },
  scrub_pants: { name: 'Scrub Pants', legendaryName: 'Emergency Kit', flavor: 'Drawstring waist. Room to kneel.' },
  paramedic_boots: { name: 'Paramedic Boots', legendaryName: 'First Responder', flavor: 'Ran every call. Still running.' },
  // Foundry (BACKLOG #36): what the Foundry's raiders wear, heavy armor of T3.
  welders_mask: { name: 'Welder’s Mask', legendaryName: 'Blind Saint', flavor: 'Welded plate and soot.' },
  welded_plate: { name: 'Welded Plate', legendaryName: 'Iron Apron', flavor: 'Welded plate and soot.' },
  furnace_gloves: { name: 'Furnace Gloves', legendaryName: 'Hands of the Forge', flavor: 'Welded plate and soot.' },
  slag_plated_trousers: { name: 'Slag-Plated Trousers', legendaryName: 'Molten Stride', flavor: 'Welded plate and soot.' },
  foundry_boots: { name: 'Foundry Boots', legendaryName: 'Warlord’s Tread', flavor: 'Welded plate and soot.' },
  // Nightstalker
  shadow_hood: { name: 'Shadow Hood', legendaryName: 'Faceless', flavor: 'Deep enough that nobody sees your eyes.' },
  stalker_coat: { name: 'Stalker Coat', legendaryName: 'Nightfall', flavor: 'Oiled canvas. It does not rustle.' },
  silk_grip_gloves: { name: 'Silk-Grip Gloves', legendaryName: 'Velvet Touch', flavor: 'Feel every lock pin, leave no prints.' },
  wrapped_pants: { name: 'Wrapped Pants', legendaryName: 'Silent Stride', flavor: 'Bound tight at the ankles. Nothing catches.' },
  soft_sole_boots: { name: 'Soft-Sole Boots', legendaryName: 'Ghost Walkers', flavor: 'Wrapped in cloth. Leave no prints worth reading.' },
  // Military
  combat_helmet: { name: 'Combat Helmet', legendaryName: 'Eye of the Storm', flavor: 'Somebody else’s name is still inside.' },
  plate_carrier: { name: 'Plate Carrier', legendaryName: 'Vaultguard', flavor: 'Heavy enough to count as a decision.' },
  tactical_gloves: { name: 'Tactical Gloves', legendaryName: 'Trigger Discipline', flavor: 'Knuckle guards, for the conversations that go badly.' },
  combat_pants: { name: 'Combat Pants', legendaryName: 'Kneel & Fire', flavor: 'Knee pads, drag handle, map pocket. Standard issue.' },
  combat_boots: { name: 'Combat Boots', legendaryName: 'Forced March', flavor: 'Broken in by somebody else.' },
  // Backpacks
  school_backpack: { name: 'School Backpack', legendaryName: 'Class Dismissed', flavor: 'Someone’s name in marker. A keychain that still jingles.' },
  duffel_sling: { name: 'Duffel Sling', legendaryName: 'Clean Getaway', flavor: 'Gym bag, field bag, everything bag.' },
  hiking_pack: { name: 'Hiking Pack', legendaryName: 'Basecamp', flavor: 'Room for everything you shouldn’t carry.' },
  medic_satchel: { name: 'Medic Satchel', legendaryName: 'Field Hospital', flavor: 'Pockets sized for bandages. Green patch, no cross.', extra: { healing: 0.1 } },
  tactical_pack: { name: 'Tactical Pack', legendaryName: 'Loadout', flavor: 'MOLLE straps for things you haven’t found yet.' },
  military_rucksack: { name: 'Military Rucksack', legendaryName: 'Nomad’s Burden', flavor: 'Built for a platoon’s worth of stubbornness.' },
  // BACKLOG #36.
  courier_bag: { name: 'Courier Bag', legendaryName: 'Toll Paid', flavor: 'Tollmen courier bag, waxed canvas.' },
  warden_field_pack: { name: 'WARDEN Field Pack', legendaryName: 'Protocol Zero', flavor: 'A city-issue pack with the crest scratched off.' },
  // Accessories (krug 5, F10): no base stat in the Item Catalog, only affixes and perks.
  cracked_wristwatch: { name: 'Cracked Wristwatch', legendaryName: 'Borrowed Time', flavor: 'Stopped at 4:12. Still keeps you on schedule.' },
  lucky_keychain: { name: 'Lucky Keychain', legendaryName: 'Rabbit’s Luck', flavor: 'A die and a key on a carabiner.' },
  dog_tags: { name: 'Dog Tags', legendaryName: 'Unbroken', flavor: 'Blank. You fill in the story.' },
  brass_compass: { name: 'Brass Compass', legendaryName: 'True North', flavor: 'Always points somewhere. Occasionally north.' },
  flip_lighter: { name: 'Flip Lighter', legendaryName: 'Eternal Flame', flavor: 'Scratched lid. Always one more click in it.' },
  hand_radio: { name: 'Hand Radio', legendaryName: 'Early Warning', flavor: 'Mostly static. Sometimes a voice reading numbers.' },
  pill_case: { name: 'Pill Case', legendaryName: 'Painless', flavor: 'Seven little doors, one for every bad day.' },
  night_monocle: { name: 'Night Monocle', legendaryName: 'Owl Eye', flavor: 'One eye sees in the dark. The other keeps watch.' },
  trophy_necklace: { name: 'Trophy Necklace', legendaryName: 'Alpha’s Fang', flavor: 'Teeth and claws on a cord. Not all of them given.' },
  service_medal: { name: 'Service Medal', legendaryName: 'Valor', flavor: 'No name, no unit. Just the ribbon.' },
};

function fromMvp(d: MvpItemData): ItemTemplate {
  const det = DETAILS[d.id as MvpItemId];
  const head = headlineStat(d.slot);
  const base: ItemStats = { ...(head ? { [head]: d.base } : {}), ...(d.class ? WEAPON_CLASS_TRAITS[d.class] : {}), ...det.extra };
  return {
    id: d.id,
    name: det.name,
    slot: d.slot,
    tier: d.tier,
    weaponClass: d.class,
    loud: det.loud,
    setId: d.setId,
    iconFolder: d.iconFolder,
    base,
    weight: d.weight,
    legendaryName: det.legendaryName,
    flavor: det.flavor,
  };
}

/** Name prefixes by rarity (Item Catalog, krug 4 E5); Legendary items have their own name. */
export const RARITY_PREFIX: Record<Exclude<Rarity, 'legendary'>, string> = { common: 'Scavenged', uncommon: 'Patched', rare: 'Reinforced', epic: 'Custom' };

/**
 * Krug 5 (K4.12): catalog names that already start with a describing word get no rarity prefix
 * ("Patched Hoodie", "Worn Sneakers", never "Scavenged Patched Hoodie"). One list, here.
 */
export const DESCRIPTIVE_FIRST_WORDS: readonly string[] = ['Patched', 'Torn', 'Worn', 'Cracked', 'Fingerless', 'Wrapped', 'Scrap', 'Rusted', 'Lucky'];

/**
 * Display name: "Reinforced Crowbar", "Custom Revolver"; Legendary items carry their own
 * name from the Item Catalog. A name that starts with a describing word or with the prefix
 * itself stays as it is; the rarity still shows in the frame colour and the tag.
 */
export function itemDisplayName(t: Pick<ItemTemplate, 'name' | 'legendaryName'>, rarity: Rarity): string {
  if (rarity === 'legendary') return t.legendaryName ?? t.name;
  const prefix = RARITY_PREFIX[rarity];
  const first = t.name.split(' ')[0];
  return t.name.startsWith(`${prefix} `) || DESCRIPTIVE_FIRST_WORDS.includes(first) ? t.name : `${prefix} ${t.name}`;
}

/** The catalog name without the rarity prefix (lists that already show the rarity). */
export function itemBaseName(t: Pick<ItemTemplate, 'name' | 'legendaryName'>, rarity: Rarity): string {
  return rarity === 'legendary' && t.legendaryName ? t.legendaryName : t.name;
}

export const ITEM_TEMPLATES: ItemTemplate[] = MVP_ITEMS.map(fromMvp);

export const ITEM_BY_ID: Record<string, ItemTemplate> = Object.fromEntries(ITEM_TEMPLATES.map((t) => [t.id, t]));

/** Template for a saved template id. Ids from before the MVP catalog resolve to their replacement. */
export function itemTemplate(id: string): ItemTemplate {
  const t = ITEM_BY_ID[id] ?? ITEM_BY_ID[currentItemId(id)];
  if (!t) throw new Error(`Unknown item template: ${id}`);
  return t;
}

/** The name the game shows; an upgraded item ends with its level (krug 5, F9): "Reinforced Crowbar +4". */
export function itemName(templateId: string, rarity: Rarity, upgrade = 0): string {
  const name = itemDisplayName(itemTemplate(templateId), rarity);
  return upgrade ? `${name} +${upgrade}` : name;
}

/** Same as itemTemplate() but returns null for ids that no longer exist (report history, share cards). */
export function findItemTemplate(id: string | null | undefined): ItemTemplate | null {
  if (!id) return null;
  return ITEM_BY_ID[id] ?? ITEM_BY_ID[currentItemId(id)] ?? null;
}

/**
 * Random affix pools by slot (krug 4, E5: the slots of the Item Catalog's affix table):
 * [stat, min, max, group?]. A group is one catalog row with several stats ("+STR / AGI /
 * INT / VIT / PER", "vs Infected / Raiders / Mutants", "−% melee / ranged damage"): the
 * roll picks a row first, then one of its stats. Catalog affixes take their range from the
 * item's tier (`AFFIX_TIER_RANGES`); min/max here are their T1 values. Affixes the catalog
 * does not list (stealth, vs survivors, craft speed, discovery, luck) keep these ranges,
 * growing with item level and rarity. Flat damage is no longer rolled (the catalog has
 * "+% damage"). Items already rolled keep their values.
 */
export type AffixPoolEntry = [ItemStatKey, number, number] | [ItemStatKey, number, number, 'attr' | 'vs' | 'resist'];

const ATTR_ROWS: AffixPoolEntry[] = (['str', 'agi', 'int', 'vit', 'per'] as const).map((k) => [k, 1, 1, 'attr']);
const VS_ROWS: AffixPoolEntry[] = (['vsInfected', 'vsRaider', 'vsMutant'] as const).map((k) => [k, 0.04, 0.06, 'vs']);
const RESIST_ROWS: AffixPoolEntry[] = [['meleeResist', 0.02, 0.03, 'resist'], ['rangedResist', 0.02, 0.03, 'resist']];

export const AFFIX_POOLS: Record<GearSlot, AffixPoolEntry[]> = {
  weapon: [['damagePct', 0.02, 0.04], ['crit', 0.01, 0.02], ['accuracy', 0.02, 0.04], ...VS_ROWS, ['ambush', 0.03, 0.05], ...ATTR_ROWS, ['durability', 0.05, 0.1], ['vsSurvivor', 0.05, 0.1]],
  helmet: [['accuracy', 0.02, 0.04], ['armor', 1, 2], ['hp', 10, 20], ...RESIST_ROWS, ...ATTR_ROWS, ['xp', 0.02, 0.03], ['detect', 0.02, 0.04], ['durability', 0.05, 0.1], ['stealth', 0.02, 0.04]],
  body: [['armor', 1, 2], ['hp', 10, 20], ['dodge', 0.01, 0.02], ...RESIST_ROWS, ...ATTR_ROWS, ['capacity', 3, 5], ['encounter', 0.01, 0.02], ['durability', 0.05, 0.1], ['stealth', 0.02, 0.04]],
  gloves: [['damagePct', 0.02, 0.04], ['crit', 0.01, 0.02], ['accuracy', 0.02, 0.04], ['ambush', 0.03, 0.05], ...ATTR_ROWS, ['loot', 0.01, 0.02], ['durability', 0.05, 0.1], ['craftSpeed', 0.05, 0.1]],
  pants: [['armor', 1, 2], ['hp', 10, 20], ['dodge', 0.01, 0.02], ...RESIST_ROWS, ...ATTR_ROWS, ['capacity', 3, 5], ['escape', 0.02, 0.04], ['durability', 0.05, 0.1]],
  boots: [['armor', 1, 2], ['dodge', 0.01, 0.02], ...ATTR_ROWS, ['expeditionTime', 0.02, 0.03], ['escape', 0.02, 0.04], ['encounter', 0.01, 0.02], ['durability', 0.05, 0.1], ['stealth', 0.02, 0.05]],
  backpack: [...ATTR_ROWS, ['capacity', 3, 5], ['loot', 0.01, 0.02], ['expeditionTime', 0.02, 0.03], ['durability', 0.05, 0.1]],
  accessory: [
    ['crit', 0.01, 0.02],
    ['ambush', 0.03, 0.05],
    ...ATTR_ROWS,
    ['loot', 0.01, 0.02],
    ['xp', 0.02, 0.03],
    ['expeditionTime', 0.02, 0.03],
    ['escape', 0.02, 0.04],
    ['encounter', 0.01, 0.02],
    ['detect', 0.02, 0.04],
    ['durability', 0.05, 0.1],
    ['discovery', 0.1, 0.2],
    ['luck', 0.03, 0.08],
  ],
};

/**
 * Item Catalog affix ranges (section "Afiksi") by item tier: the catalog lists T1 and T4,
 * T2 and T3 lie evenly between them. The tier is the item's catalog tier (Kitchen Knife T1,
 * Shock Machete T4), not the level it dropped at.
 */
export const AFFIX_TIER_RANGES: Partial<Record<ItemStatKey, { t1: [number, number]; t4: [number, number] }>> = {
  accuracy: { t1: [0.02, 0.04], t4: [0.06, 0.1] },
  crit: { t1: [0.01, 0.02], t4: [0.04, 0.06] },
  vsInfected: { t1: [0.04, 0.06], t4: [0.12, 0.18] },
  vsRaider: { t1: [0.04, 0.06], t4: [0.12, 0.18] },
  vsMutant: { t1: [0.04, 0.06], t4: [0.12, 0.18] },
  armor: { t1: [1, 2], t4: [5, 8] },
  hp: { t1: [10, 20], t4: [60, 90] },
  dodge: { t1: [0.01, 0.02], t4: [0.04, 0.06] },
  meleeResist: { t1: [0.02, 0.03], t4: [0.06, 0.09] },
  rangedResist: { t1: [0.02, 0.03], t4: [0.06, 0.09] },
  str: { t1: [1, 1], t4: [3, 4] },
  agi: { t1: [1, 1], t4: [3, 4] },
  int: { t1: [1, 1], t4: [3, 4] },
  vit: { t1: [1, 1], t4: [3, 4] },
  per: { t1: [1, 1], t4: [3, 4] },
  capacity: { t1: [3, 5], t4: [12, 18] },
  loot: { t1: [0.01, 0.02], t4: [0.04, 0.06] },
  xp: { t1: [0.02, 0.03], t4: [0.06, 0.08] },
  escape: { t1: [0.02, 0.04], t4: [0.06, 0.1] },
  detect: { t1: [0.02, 0.04], t4: [0.08, 0.12] },
  // Krug 4 (E5): the rest of the catalog table.
  damagePct: { t1: [0.02, 0.04], t4: [0.08, 0.12] },
  ambush: { t1: [0.03, 0.05], t4: [0.1, 0.15] },
  expeditionTime: { t1: [0.02, 0.03], t4: [0.05, 0.08] },
  encounter: { t1: [0.01, 0.02], t4: [0.04, 0.06] },
  durability: { t1: [0.05, 0.1], t4: [0.2, 0.3] },
};

/** Highest growth of a catalog affix inside its tier (A1): ×1.15 at the tier's top level. */
export const AFFIX_TIER_GROWTH = 0.15;

export interface PassiveDef {
  id: string;
  name: string;
  text: string;
  slots: GearSlot[];
  /** Old Legendary unique: kept working on existing items, never rolled. */
  legendary?: boolean;
}

export const PASSIVES: Record<string, PassiveDef> = {
  bane_raiders: { id: 'bane_raiders', name: 'Raider Bane', text: '+10% damage against Raiders', slots: ['weapon'] },
  bane_infected: { id: 'bane_infected', name: 'Cleanser', text: '+10% damage against Infected', slots: ['weapon'] },
  bane_mutants: { id: 'bane_mutants', name: 'Brute Breaker', text: '+12% damage against Mutants', slots: ['weapon'] },
  bane_survivors: { id: 'bane_survivors', name: 'Duelist', text: '+8% damage against other survivors', slots: ['weapon'] },
  first_strike: { id: 'first_strike', name: 'First Strike', text: '+15% damage in the first round', slots: ['weapon'] },
  finisher: { id: 'finisher', name: 'Finisher', text: '+15% damage against targets under 30% HP', slots: ['weapon'] },
  thick_skin: { id: 'thick_skin', name: 'Thick Skin', text: '−5% damage taken', slots: ['helmet', 'body', 'pants'] },
  second_wind: { id: 'second_wind', name: 'Second Wind', text: 'Heal 5% HP after winning a fight', slots: ['body', 'helmet'] },
  unseen: { id: 'unseen', name: 'Unseen', text: '+5% stealth', slots: ['body', 'helmet', 'pants', 'boots'] },
  sturdy: { id: 'sturdy', name: 'Sturdy', text: 'Loses durability half as fast', slots: ['body', 'helmet', 'pants', 'boots', 'weapon'] },
  fleet: { id: 'fleet', name: 'Fleet', text: '+8% escape chance', slots: ['boots', 'pants'] },
  silent_soles: { id: 'silent_soles', name: 'Silent Soles', text: '−10% encounter chance', slots: ['boots'] },
  deep_pockets: { id: 'deep_pockets', name: 'Deep Pockets', text: '+10% capacity', slots: ['backpack', 'pants'] },
  scavenger_eye: { id: 'scavenger_eye', name: 'Scavenger’s Eye', text: '+6% loot', slots: ['backpack', 'accessory'] },
  light_load: { id: 'light_load', name: 'Light Load', text: 'A full pack costs half as much escape chance', slots: ['backpack'] },
  steady_hands: { id: 'steady_hands', name: 'Steady Hands', text: '+5% critical chance', slots: ['gloves', 'weapon'] },
  tinker: { id: 'tinker', name: 'Tinker', text: '+15% crafting speed', slots: ['gloves'] },
  lucky: { id: 'lucky', name: 'Lucky', text: '+5% chance to find an extra item', slots: ['accessory'] },
  lookout: { id: 'lookout', name: 'Lookout', text: '+10% chance to spot a Shadow', slots: ['accessory', 'helmet'] },
  homing: { id: 'homing', name: 'Homing', text: '−10% expedition time', slots: ['accessory'] },
  // Perks of the Legendary uniques from before the MVP catalog. They are no longer rolled;
  // items that already have one keep it. Catalog Legendary perks are a separate task.
  ember: { id: 'ember', name: 'Ember', text: '+25% damage against Infected and Mutants. Critical hits deal 2× damage.', slots: ['weapon'], legendary: true },
  quiet_one: { id: 'quiet_one', name: 'Silence', text: 'Never raises encounter chance. Attacks from ambush always crit.', slots: ['weapon'], legendary: true },
  eye_storm: { id: 'eye_storm', name: 'Storm Sight', text: '+25% chance to spot Shadows. Ambushes against you lose their bonus.', slots: ['helmet'], legendary: true },
  vaultguard: { id: 'vaultguard', name: 'Vault Door', text: '−10% damage taken. Lose half as much when defeated in PvP.', slots: ['body'], legendary: true },
  ghost_walkers: { id: 'ghost_walkers', name: 'No Prints', text: '+10% stealth. −20% chance of meeting Infected and Raiders.', slots: ['boots'], legendary: true },
  nomads_burden: { id: 'nomads_burden', name: 'Walking Camp', text: 'A full pack no longer lowers escape chance.', slots: ['backpack'], legendary: true },
};

/** Passives an item of this slot can roll (Epic and Legendary always get one, Rare sometimes). */
export function passivesForSlot(slot: GearSlot): PassiveDef[] {
  return Object.values(PASSIVES).filter((p) => !p.legendary && p.slots.includes(slot));
}

/** Level range per tier. */
export const TIER_LEVELS: Record<1 | 2 | 3 | 4, [number, number]> = { 1: [1, 5], 2: [6, 10], 3: [11, 15], 4: [16, 20] };

/**
 * 1. 10. 2026 (C4): an item's tier: its own after a tier upgrade (or a Relic Forge item made on the survivor's
 * tier), else its template's.
 */
export function itemTier(item: { templateId: string; tier?: number | null }): 1 | 2 | 3 | 4 {
  return (item.tier as 1 | 2 | 3 | 4 | null | undefined) ?? itemTemplate(item.templateId).tier;
}

/**
 * Krug 5 (F8.4): the first level of an item's tier (T2 6, T3 11, T4 16): until 23. 10. 2026 the level a survivor needed to
 * wear it; since then the level it has its full stats from (`TIER_WEAR`, `wearLevel` for putting it on).
 * `tier`: the item's own tier (C4), when it has one.
 */
export function requiredLevel(templateId: string, tier?: number | null): number {
  const t = findItemTemplate(templateId);
  const n = (tier as 1 | 2 | 3 | 4 | null | undefined) ?? t?.tier;
  return n ? TIER_LEVELS[n][0] : 1;
}

/**
 * 23. 10. 2026 (owner, BACKLOG #55, a player's feedback): an item up to `ahead` tiers above the survivor's may be worn right
 * away, with the stats of the top of the survivor's tier until the survivor reaches the item's tier (`wornAs`,
 * items/tierWear.ts); from that level it has its full stats. Two tiers above still waits. `on: false` (simulations,
 * TIER_WEAR=old): the rule before, an item only from its tier's first level. Mutable only for the simulations and tests.
 */
export const TIER_WEAR = { on: true, ahead: 1 };

/** The first level a survivor may put the item on: its tier's first level, or with `TIER_WEAR` the first level of the tier `ahead` below it. */
export function wearLevel(templateId: string, tier?: number | null): number {
  const t = findItemTemplate(templateId);
  const n = (tier as 1 | 2 | 3 | 4 | null | undefined) ?? t?.tier;
  if (!n) return 1;
  const from = TIER_WEAR.on ? Math.max(1, n - Math.max(0, Math.floor(TIER_WEAR.ahead))) : n;
  return TIER_LEVELS[from as 1 | 2 | 3 | 4][0];
}

/** Whether a survivor of this level may put the item on (`wearLevel`; below `requiredLevel` it is worn capped, `wornAs`). */
export function canWear(level: number, templateId: string, tier?: number | null): boolean {
  return level >= wearLevel(templateId, tier);
}

export function tierForLevel(level: number): 1 | 2 | 3 | 4 {
  if (level <= 5) return 1;
  if (level <= 10) return 2;
  if (level <= 15) return 3;
  return 4;
}
